import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";

function ConstellationCanvas({ color }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const ctx = canvas.getContext("2d");
    const stars = Array.from({length: 100}, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.15, vy: (Math.random() - 0.5) * 0.15,
      r: Math.random() * 1.5 + 0.3,
      twinkle: Math.random() * Math.PI * 2, twinkleSpeed: Math.random() * 0.02 + 0.01,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.x += s.vx; s.y += s.vy; s.twinkle += s.twinkleSpeed;
        if (s.x < 0 || s.x > canvas.width)  s.vx *= -1;
        if (s.y < 0 || s.y > canvas.height) s.vy *= -1;
        const alpha = 0.4 + 0.4 * Math.sin(s.twinkle);
        ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`; ctx.fill();
      });
      stars.forEach((a, i) => stars.slice(i + 1).forEach(b => {
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (d < 130) {
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
          ctx.strokeStyle = `rgba(255,255,255,${0.08 * (1 - d / 130)})`;
          ctx.lineWidth = 0.5; ctx.stroke();
        }
      }));
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [color]);
  return <canvas ref={canvasRef} style={{ position:"fixed", inset:0, width:"100%", height:"100%", pointerEvents:"none", zIndex:0 }} />;
}

export default function ComingSoon() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const company   = location.state?.company || { name:"Company", color:"#7c3aed", g2:"#bf5fff", logo:"" };

  return (
    <div style={{
      minHeight:"100vh", display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center",
      background:`radial-gradient(ellipse at 30% 40%, ${company.color}33 0%, #040d1a 60%)`,
      fontFamily:"Inter,sans-serif", position:"relative", overflow:"hidden",
    }}>
      <ConstellationCanvas color={company.color} />

      {/* Glow blobs */}
      <div style={{ position:"fixed", top:-100, left:-100, width:500, height:500, borderRadius:"50%",
        background:`radial-gradient(circle,${company.color}25,transparent 70%)`, pointerEvents:"none" }} />
      <div style={{ position:"fixed", bottom:-100, right:-100, width:400, height:400, borderRadius:"50%",
        background:`radial-gradient(circle,${company.g2}18,transparent 70%)`, pointerEvents:"none" }} />

      {/* Back button */}
      <button onClick={() => navigate("/")} style={{
        position:"fixed", top:28, left:32,
        background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)",
        borderRadius:50, padding:"9px 22px", cursor:"pointer",
        color:"rgba(255,255,255,0.8)", fontSize:13, fontWeight:700,
        display:"flex", alignItems:"center", gap:8, backdropFilter:"blur(12px)",
        transition:"all 0.2s", zIndex:10 }}
        onMouseEnter={e => { e.currentTarget.style.background=`${company.color}33`; e.currentTarget.style.color="#fff"; }}
        onMouseLeave={e => { e.currentTarget.style.background="rgba(255,255,255,0.08)"; e.currentTarget.style.color="rgba(255,255,255,0.8)"; }}>
        ← Back to TSI Groups
      </button>

      {/* Main card */}
      <div style={{
        position:"relative", zIndex:1,
        display:"flex", flexDirection:"column", alignItems:"center",
        gap:32, textAlign:"center", padding:"0 24px", maxWidth:600,
      }}>

        {/* Logo */}
        <div style={{
          width:140, height:140, borderRadius:36, overflow:"hidden",
          background:"white", display:"flex", alignItems:"center", justifyContent:"center",
          border:`3px solid ${company.color}88`,
          boxShadow:`0 0 0 8px ${company.color}18, 0 20px 60px ${company.color}44`,
          animation:"floatY 3s ease-in-out infinite",
        }}>
          <img src={company.logo} alt={company.name}
            style={{ width:"88%", height:"88%", objectFit:"contain" }}
            onError={e => {
              e.target.style.display="none";
              e.target.parentNode.style.background=`linear-gradient(135deg,${company.color},${company.g2})`;
              e.target.parentNode.style.color="white";
              e.target.parentNode.style.fontWeight="900";
              e.target.parentNode.style.fontSize="28px";
              e.target.parentNode.innerText=company.name;
            }} />
        </div>

        {/* Company name */}
        <div>
          <div style={{ fontSize:13, fontWeight:800, letterSpacing:3, textTransform:"uppercase",
            color:company.color, marginBottom:10 }}>{company.name} Dashboard</div>
          <h1 style={{ margin:0, fontSize:52, fontWeight:900, color:"#fff", lineHeight:1.1,
            textShadow:`0 0 40px ${company.color}50` }}>
            Coming<br/>Soon
          </h1>
        </div>

        {/* Status badge */}
        <div style={{
          background:`${company.color}18`, border:`1px solid ${company.color}44`,
          borderRadius:50, padding:"10px 28px",
          display:"flex", alignItems:"center", gap:10,
        }}>
          <span style={{ fontSize:18 }}>⚙️</span>
          <span style={{ color:"rgba(255,255,255,0.9)", fontSize:14, fontWeight:700, letterSpacing:0.5 }}>
            Currently Under Development
          </span>
        </div>

        {/* Description */}
        <p style={{ margin:0, color:"rgba(255,255,255,0.5)", fontSize:15, lineHeight:1.7, fontWeight:500, maxWidth:480 }}>
          The <strong style={{ color:"rgba(255,255,255,0.85)" }}>{company.name}</strong> analytics dashboard is being built and will be available soon.
          Check back later or contact your administrator for updates.
        </p>

        {/* Progress bar */}
        <div style={{ width:"100%", maxWidth:380 }}>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.4)", fontWeight:700, letterSpacing:1.5, textTransform:"uppercase" }}>Development Progress</span>
            <span style={{ fontSize:11, color:company.color, fontWeight:800 }}>In Progress</span>
          </div>
          <div style={{ height:6, background:"rgba(255,255,255,0.08)", borderRadius:10, overflow:"hidden" }}>
            <div style={{
              height:"100%", borderRadius:10, width:"40%",
              background:`linear-gradient(90deg,${company.color},${company.g2})`,
              boxShadow:`0 0 12px ${company.color}`,
              animation:"progressPulse 2s ease-in-out infinite",
            }}/>
          </div>
        </div>

        {/* Back button */}
        <button onClick={() => navigate("/")} style={{
          background:`linear-gradient(135deg,${company.color},${company.g2})`,
          color:"white", border:"none", borderRadius:50, padding:"14px 40px",
          fontWeight:800, fontSize:15, cursor:"pointer",
          boxShadow:`0 8px 28px ${company.color}50`, letterSpacing:0.3,
          transition:"all 0.2s" }}
          onMouseEnter={e => { e.currentTarget.style.transform="translateY(-3px) scale(1.04)"; e.currentTarget.style.boxShadow=`0 14px 36px ${company.color}60`; }}
          onMouseLeave={e => { e.currentTarget.style.transform="translateY(0) scale(1)"; e.currentTarget.style.boxShadow=`0 8px 28px ${company.color}50`; }}>
          ← Back to TSI Groups
        </button>
      </div>

      <style>{`
        @keyframes floatY { 0%,100%{transform:translateY(-6px)} 50%{transform:translateY(-16px)} }
        @keyframes progressPulse { 0%,100%{opacity:1} 50%{opacity:0.6} }
        * { box-sizing:border-box; }
      `}</style>
    </div>
  );
}